import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/landing/Navbar';
import { Footer } from '@/components/landing/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Shield,
  Eye,
  Database,
  Lock,
  UserCheck,
  Clock,
  Mail,
  FileText,
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const highlights = [
  { icon: Shield, title: 'Enterprise Security', description: 'AES-256 encryption at rest, TLS 1.3 in transit.' },
  { icon: Eye, title: 'Full Transparency', description: 'We never sell your data. Period.' },
  { icon: UserCheck, title: 'Your Rights', description: 'Access, correct, or delete your data anytime.' },
  { icon: Clock, title: '90-Day Retention', description: 'Conversation logs auto-delete after 90 days.' },
];

const sections = [
  {
    id: 'info-collected',
    icon: Database,
    title: 'Information We Collect',
    content: [
      {
        subtitle: 'Personal Information',
        items: [
          'Name, email address, and phone number provided during registration',
          'Business name, address, and industry information',
          'Payment and billing information processed through our secure payment providers',
          'WhatsApp number connected to your Agentick account',
        ],
      },
      {
        subtitle: 'Usage Information',
        items: [
          'Conversation logs between your AI agent and customers',
          'Knowledge base documents you upload to the platform',
          'Platform usage patterns and feature engagement analytics',
          'Device information, browser type, and IP address',
        ],
      },
    ],
  },
  {
    id: 'how-we-use',
    icon: FileText,
    title: 'How We Use Your Information',
    content: [
      {
        subtitle: null,
        items: [
          'To provide, maintain, and improve our AI chatbot services',
          'To process your transactions and manage your account',
          'To train and improve AI models that power your chatbot (anonymized data only)',
          'To send service-related communications, updates, and security alerts',
          'To detect, prevent, and address technical issues and security threats',
          'To comply with legal obligations and enforce our terms of service',
        ],
      },
    ],
  },
  {
    id: 'data-sharing',
    icon: UserCheck,
    title: 'Data Sharing & Disclosure',
    paragraphs: [
      'We do not sell your personal information to third parties. We may share your information only in the following limited circumstances:',
    ],
    content: [
      {
        subtitle: null,
        items: [
          'With service providers who assist in operating our platform (hosting, analytics, payment processing)',
          'When required by law, regulation, or valid legal process',
          'To protect the rights, property, or safety of Agentick, our users, or the public',
          'In connection with a merger, acquisition, or sale of assets (with prior notice to users)',
        ],
      },
    ],
  },
  {
    id: 'security',
    icon: Lock,
    title: 'Data Security',
    paragraphs: [
      'We implement industry-standard security measures including encryption in transit (TLS 1.3) and at rest (AES-256), regular security audits, strict access controls, and secure development practices.',
      'Our infrastructure is hosted on enterprise-grade cloud providers with SOC 2 Type II and ISO 27001 certifications. All data transfers use encrypted channels, and access to customer data is restricted through role-based controls with multi-factor authentication.',
    ],
  },
  {
    id: 'retention',
    icon: Clock,
    title: 'Data Retention',
    paragraphs: [
      'We retain your personal information for as long as your account is active or as needed to provide services. Conversation logs are retained for 90 days by default, after which they are automatically deleted.',
      'Upon account deletion, all associated data including knowledge base documents, conversation history, and lead information will be permanently removed within 30 days. You may request earlier deletion at any time by contacting our support team.',
    ],
  },
  {
    id: 'your-rights',
    icon: UserCheck,
    title: 'Your Rights',
    paragraphs: ['Under applicable data protection laws, you have the following rights:'],
    content: [
      {
        subtitle: null,
        items: [
          'Access and receive a copy of all personal data we hold about you',
          'Correct inaccurate or incomplete personal data',
          'Request deletion of your personal data (“right to be forgotten”)',
          'Object to processing of your personal data for specific purposes',
          'Data portability — receive your data in a structured, machine-readable format',
          'Withdraw consent at any time for consent-based processing',
        ],
      },
    ],
  },
];

function SectionBlock({ section, index }) {
  const [ref, isVisible] = useScrollAnimation(0.1);

  return (
    <motion.section
      ref={ref}
      id={section.id}
      initial={{ opacity: 0, y: 20 }}
      animate={isVisible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="glass-card rounded-2xl p-7 lg:p-8"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
          <section.icon size={16} className="text-primary" />
        </div>
        <h2 className="font-heading text-lg font-semibold text-foreground">{section.title}</h2>
      </div>

      {section.paragraphs && section.paragraphs.map((p, i) => (
        <p key={i} className="text-sm text-muted-foreground font-body leading-relaxed mb-4">{p}</p>
      ))}

      {section.content && section.content.map((block, bi) => (
        <div key={bi} className={bi > 0 ? 'mt-4' : ''}>
          {block.subtitle && (
            <h3 className="font-heading text-sm font-semibold text-foreground mb-2">{block.subtitle}</h3>
          )}
          <ul className="space-y-2">
            {block.items.map((item, ii) => (
              <li key={ii} className="flex items-start gap-2.5 text-sm text-muted-foreground font-body leading-relaxed">
                <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-2" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </motion.section>
  );
}

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-20 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[400px] rounded-full bg-primary/[0.04] blur-[120px]" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(hsl(210 20% 95% / 0.1) 1px, transparent 1px), linear-gradient(90deg, hsl(210 20% 95% / 0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }} />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} transition={{ duration: 0.4 }} className="mb-8">
            <Button variant="glass" size="sm" onClick={() => navigate('/')} className="gap-2">
              <ArrowLeft size={14} /> Back to Home
            </Button>
          </motion.div>

          <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }} className="flex items-center gap-3 mb-5">
            <Badge variant="premium">
              <Shield size={12} className="mr-1" /> Privacy
            </Badge>
            <span className="text-xs text-muted-foreground font-body">Last updated: January 15, 2025</span>
          </motion.div>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-5 leading-tight"
          >
            Privacy Policy
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-base text-muted-foreground font-body leading-relaxed max-w-2xl"
          >
            At Agentick, your privacy is fundamental to everything we build. This policy explains how we collect, use, and protect your information when you use our WhatsApp AI chatbot platform.
          </motion.p>
        </div>
      </section>

      {/* Highlight cards */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-14">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {highlights.map((h, i) => (
            <motion.div
              key={h.title}
              {...fadeUp}
              transition={{ duration: 0.4, delay: 0.4 + i * 0.08 }}
              className="glass-card rounded-xl p-5 text-center"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <h.icon size={18} className="text-primary" />
              </div>
              <h3 className="font-heading text-xs font-semibold text-foreground mb-1">{h.title}</h3>
              <p className="text-[11px] text-muted-foreground font-body leading-snug">{h.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Policy Sections */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="space-y-6">
          {sections.map((section, i) => (
            <SectionBlock key={section.id} section={section} index={i} />
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="glass-card rounded-2xl p-8 text-center glow-border-primary"
        >
          <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center mx-auto mb-4">
            <Mail size={20} className="text-primary" />
          </div>
          <h2 className="font-heading text-lg font-bold text-foreground mb-2">Questions About Privacy?</h2>
          <p className="text-sm text-muted-foreground font-body mb-5 max-w-md mx-auto">
            For any questions about this Privacy Policy or our data practices, please reach out to our team.
          </p>
          <Button variant="premium" size="default" asChild>
            <a href="mailto:hello@agentick.in">Contact privacy@agentick.in</a>
          </Button>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
